import moment from "moment";
import { CalendarEvent } from "components/Calendar/types";
import type { PreviewAction, PreviewActionType } from "./types";

// Default duration (in minutes) used when rendering non-meeting actions
// on the calendar — gives them a small but visible footprint.
const DEFAULT_DURATION_MINUTES = 30;

const STYLE_BY_TYPE: Record<PreviewActionType, { bg: string; border: string }> = {
  task: { bg: "#FEF3C7", border: "#F59E0B" },
  email: { bg: "#DBEAFE", border: "#3B82F6" },
  message: { bg: "#E0E7FF", border: "#6366F1" },
  meeting: { bg: "#D1FAE5", border: "#10B981" },
};

/**
 * Convert a preview action returned by the backend into a CalendarEvent
 * suitable for MonthCalendar / WeekCalendar.
 *
 * Immediate actions are skipped because they have no scheduled time and
 * therefore have no place on a calendar — they should be surfaced in the
 * list view instead.
 */
export function previewActionToCalendarEvent(action: PreviewAction, timezone: string): CalendarEvent | null {
  if (!action.scheduledAt) return null;

  const startMoment = moment.tz(action.scheduledAt, timezone);
  const duration = action.durationMinutes ?? DEFAULT_DURATION_MINUTES;

  return {
    id: action.previewId,
    title: action.name,
    start: startMoment.toDate(),
    end: startMoment.clone().add(duration, "minutes").toDate(),
    canDrag: false,
    canResize: false,
    showEnd: action.actionType === "meeting",
    style: STYLE_BY_TYPE[action.actionType],
    eventType: action.actionType,
    data: action,
  };
}

export function previewActionsToCalendarEvents(actions: PreviewAction[], timezone: string): CalendarEvent[] {
  return actions
    .map((a) => previewActionToCalendarEvent(a, timezone))
    .filter((event): event is CalendarEvent => event !== null);
}

/**
 * Group actions by relative day from the start date.
 * Returns an array of { label, actions } in chronological order.
 * Immediate actions are grouped under "Immediate".
 */
export interface PreviewActionGroup {
  label: string;
  actions: PreviewAction[];
}

export function groupActionsByRelativeDay(
  actions: PreviewAction[],
  startDate: string,
  timezone: string,
): PreviewActionGroup[] {
  const immediate: PreviewAction[] = [];
  const byDay: Map<number, PreviewAction[]> = new Map();
  const start = moment.tz(startDate, timezone).startOf("day");

  actions.forEach((action) => {
    if (action.isImmediate || !action.scheduledAt) {
      immediate.push(action);
      return;
    }
    const dayOffset = moment.tz(action.scheduledAt, timezone).startOf("day").diff(start, "days");
    const bucket = byDay.get(dayOffset) ?? [];
    bucket.push(action);
    byDay.set(dayOffset, bucket);
  });

  const groups: PreviewActionGroup[] = [];
  if (immediate.length > 0) {
    groups.push({ label: "Immediate (before start)", actions: immediate });
  }

  Array.from(byDay.keys())
    .sort((a, b) => a - b)
    .forEach((dayOffset) => {
      const dayActions = byDay.get(dayOffset) ?? [];
      const date = start.clone().add(dayOffset, "days");
      const label =
        dayOffset === 0
          ? `Day 1 — ${date.format("ddd, MMM D")}`
          : `Day ${dayOffset + 1} — ${date.format("ddd, MMM D")}`;
      groups.push({ label, actions: dayActions });
    });

  return groups;
}
